import { Link } from "react-router-dom";
import { Clock, User, CalendarClock, BellRing } from "lucide-react";
import { PriorityBadge, SlaBadge, StatusBadge } from "@/components/common/StatusBadge";
import { SERVICE_META, TICKET_STATUS, fmtRemaining, fmtDateTime, sortByPriority, fmtRp } from "@/lib/constants";
import { cn } from "@/lib/utils";

export function TicketCard({ t }) {
  const critical = t.priority === "critical";
  return (
    <Link to={`/tickets/${t.id}`} data-testid={`ticket-card-${t.code}`} data-priority={t.priority}
      className={cn("block rounded-md border bg-card p-3 text-sm shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md",
        critical && "border-rose-400/70 bg-rose-100 text-rose-950 dark:bg-rose-500/20 dark:text-rose-50 shadow-rose-500/20",
        !critical && t.is_overdue && "border-red-500/50")}>
      <div className="flex items-center justify-between gap-2">
        <span className={cn("font-mono text-xs", critical ? "text-rose-700 dark:text-rose-200" : "text-muted-foreground")}>{t.code}</span>
        <PriorityBadge priority={t.priority} />
      </div>
      <div className="mt-1 line-clamp-2 font-semibold leading-snug">{t.title}</div>
      {t.next_step_pending && <div className="mt-1 flex items-center gap-1 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300" data-testid={`next-step-flag-${t.code}`}><BellRing className="h-3 w-3" />Langkah lanjutan belum dikonfirmasi</div>}
      {(t.details?.order_mode || t.over_budget) && <div className="mt-1 flex flex-wrap gap-1 text-[10px]">{t.details?.order_mode && <span className={cn("rounded px-1 font-mono", t.details.order_mode === "Batch" ? "bg-purple-500/15 text-purple-600" : "bg-sky-500/15 text-sky-600")} data-testid={`order-mode-${t.code}`}>{t.details.order_mode}{Array.isArray(t.details?.items) ? ` · ${t.details.items.length} item` : ""}</span>}{t.over_budget && <span className="rounded bg-red-500/15 px-1 font-semibold text-red-600" data-testid={`over-budget-${t.code}`}>Melebihi budget</span>}</div>}
      {t.needed_at && <div className={cn("mt-1 flex items-center gap-1 font-mono text-[11px]", critical ? "text-rose-700 dark:text-rose-200" : "text-amber-600 dark:text-amber-400")}><CalendarClock className="h-3 w-3" />Butuh: {fmtDateTime(t.needed_at)}</div>}
      <div className={cn("mt-1 text-xs", critical ? "text-rose-700/80 dark:text-rose-200/80" : "text-muted-foreground")}>{SERVICE_META[t.service_type]?.title}</div>
      <div className="mt-1 flex items-center justify-between gap-2 text-[11px]"><span className="font-mono" data-testid={`ticket-cost-${t.code}`}>{fmtRp(t.cost ?? 0)}</span>{t.approval_progress && t.status === "pending_approval" && <span className="truncate text-blue-600 dark:text-blue-400">{t.approval_progress}</span>}{t.rac_progress && <span className="rounded bg-pink-500/15 px-1.5 font-mono text-pink-600" title="Progres order RAC (selesai/total)" data-testid={`ticket-rac-progress-${t.code}`}>RAC {t.rac_progress.done}/{t.rac_progress.total}</span>}</div>
      <div className="mt-2 flex items-center justify-between gap-2 text-xs">
        <span className={cn("flex items-center gap-1", critical ? "text-rose-700/80 dark:text-rose-200/80" : "text-muted-foreground")}><User className="h-3 w-3" />{t.assignee_name || t.requester_name}</span>
        {t.sla_remaining_seconds != null && (
          <span className={cn("flex items-center gap-1 font-mono", t.sla_state === "breached" ? "text-red-500" : t.sla_state === "warning" ? "text-amber-500" : "text-emerald-500")}>
            <Clock className="h-3 w-3" />{fmtRemaining(t.sla_remaining_seconds)}
          </span>
        )}
      </div>
    </Link>
  );
}

const COLS = [
  { key: "pending_approval", statuses: ["pending_approval", "revise"], label: "Approval" },
  { key: "open", statuses: ["open"], label: "Open" },
  { key: "in_progress", statuses: ["in_progress"], label: "In Progress" },
  { key: "on_hold", statuses: ["on_hold"], label: "On Hold" },
  { key: "resolved", statuses: ["resolved", "closed"], label: "Resolved / Closed" },
];

export function KanbanBoard({ tickets = [] }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5" data-testid="kanban-board">
      {COLS.map((c) => {
        const items = sortByPriority(tickets.filter((t) => c.statuses.includes(t.status)));
        return (
          <div key={c.key} className="rounded-lg border bg-muted/40 p-2" data-testid={`ticket-kanban-column-${c.key}`}>
            <div className="mb-2 flex items-center justify-between px-1">
              <StatusBadge status={c.key} map={TICKET_STATUS} />
              <span className="font-mono text-xs text-muted-foreground">{items.length}</span>
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto scrollbar-thin pr-0.5">
              {items.map((t) => <TicketCard key={t.id} t={t} />)}
              {items.length === 0 && <div className="py-6 text-center text-xs text-muted-foreground">Kosong</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function TicketRowBadges({ t }) {
  return <div className="flex gap-1"><StatusBadge status={t.status} map={TICKET_STATUS} /><SlaBadge state={t.sla_state} /></div>;
}
